import React, { useState, useEffect, useCallback } from 'react'
import { Stack, FormControlLabel, Checkbox, IconButton, Link } from '@mui/material'
import List, { Row } from './list'
import ListItem from './listItem'
import { grayText2 } from '@/constant'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import TreeNode, { TreeDataProps } from './treeNode'

export interface TransferListProps<T extends { [key: string]: unknown }> {
  data: T[]
  id: string
  searchKey: string
  checked: Array<number | string>
  onChange: (chekcedIds: Array<number | string>) => void
  treeView?: boolean
  treeData?: TreeDataProps[]
}

const TransferList: <T extends { [key: string]: unknown }>(
  props: TransferListProps<T>,
) => React.ReactElement<TransferListProps<T>> = (props) => {
  const { data, treeData, treeView, searchKey, id, checked = [], onChange } = props

  const [selectedItems, setselectedItems] = useState<Array<number | string>>(checked)

  const handleSelectAll = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      // @ts-ignore
      const checked = event?.target?.checked as boolean
      if (checked) {
        const selected = (data?.map((item) => item?.[id]) ?? []) as string[]
        setselectedItems(selected)
      } else {
        setselectedItems([])
      }
    },
    [data, id],
  )

  const handleDeSelect = useCallback(
    (selectedIds: number[]) => () => {
      const updatedIds = [...selectedItems]?.filter((item) => selectedIds?.indexOf(item as number) === -1)
      setselectedItems(updatedIds)
    },
    [selectedItems],
  )

  const handleResetSelection = useCallback(() => {
    setselectedItems([])
  }, [])

  const handleSelect = useCallback(
    (selected: boolean, selectedIds: number[]) => {
      if (selected) {
        const updatedIds = [...selectedItems]
        updatedIds.push(...selectedIds)
        const idsSet = new Set(updatedIds)
        const idsArray = Array.from(idsSet)
        setselectedItems(idsArray)
      } else {
        handleDeSelect(selectedIds)()
      }
    },
    [handleDeSelect, selectedItems],
  )

  const handleSelectListItem = useCallback(
    (selectedIds: number[]) => (selected: boolean) => {
      if (selected) {
        const updatedIds = [...selectedItems]
        updatedIds.push(...selectedIds)
        setselectedItems(updatedIds)
      } else {
        handleDeSelect(selectedIds)()
      }
    },
    [handleDeSelect, selectedItems],
  )

  const renderItem = useCallback(
    (item: TreeDataProps, index: number, onClick: (itemId: string) => void) => {
      return treeView ? (
        <TreeNode
          key={`list-item-${index}`}
          data={item}
          checked={selectedItems}
          // @ts-ignore
          onNodeChange={handleSelect}
          onItemClick={onClick}
        />
      ) : (
        <ListItem
          key={`list-item-${index}`}
          checkable={true}
          // @ts-ignore
          text={item?.[searchKey]}
          selected={
            selectedItems?.findIndex(
              // @ts-ignore
              (selectedId) => selectedId === item?.[id],
            ) > -1
          }
          // @ts-ignore
          onSelect={handleSelectListItem([item?.[id]])}
        />
      )
    },
    [handleSelect, handleSelectListItem, id, searchKey, selectedItems, treeView],
  )

  const renderListItem = useCallback(
    (item: TreeDataProps, index: number) => (
      <ListItem
        key={`list-item-${index}`}
        checkable={false}
        // @ts-ignore
        text={item?.[searchKey]}
        // @ts-ignore
        onDelete={handleDeSelect([item?.[id]])}
      />
    ),
    [handleDeSelect, id, searchKey],
  )

  useEffect(() => {
    setselectedItems(checked)
  }, [checked])

  useEffect(() => {
    onChange(selectedItems)
  }, [onChange, selectedItems])

  return (
    <Row sx={{ flex: 1, justifyContent: 'normal', gap: '8px', overflow: 'auto' }}>
      <List
        sx={{ flex: 1 }}
        data={data ?? []}
        header={
          <>
            <FormControlLabel
              label='全选'
              // @ts-ignore
              onChange={handleSelectAll}
              control={
                <Checkbox
                  indeterminate={
                    data?.findIndex(
                      (item) => selectedItems?.findIndex((selectedId) => selectedId === item?.[id]) > -1,
                    ) > -1 && data?.length !== selectedItems?.length
                  }
                  checked={data?.length === selectedItems?.length && data?.length > 0}
                />
              }
            />
            <span>{data?.length > 0 && `${selectedItems?.length ?? 0} / ${data?.length}`}</span>
          </>
        }
        searchKey={searchKey}
        treeView={treeView}
        treeData={treeData}
        // @ts-ignore
        renderItem={renderItem}
      />
      <Stack sx={{ justifyContent: 'center', gap: '8px' }}>
        <IconButton disabled={true} sx={{ backgroundColor: grayText2 }}>
          <KeyboardArrowRightIcon />
        </IconButton>
      </Stack>
      <List
        header={
          <>
            {`已选 ${selectedItems?.length ?? 0} 项`}
            <Link underline='none' onClick={handleResetSelection}>
              清空选择
            </Link>
          </>
        }
        sx={{ flex: 1 }}
        data={data.filter((item) => selectedItems?.findIndex((checkedId) => checkedId === item?.[id]) > -1) ?? []}
        searchKey={searchKey}
        // @ts-ignore
        renderItem={renderListItem}
      />
    </Row>
  )
}

export default TransferList
